import { Field as BaseField } from "rc-field-form";
import { useCallback, useState } from "react";
import moment from "moment-timezone";
import Field from "../../../../components/Field";
import DateInput from "../../../../components/Inputs/DateInput";
import { formatDatesInText } from "../../../../utils/helpers/date";

const today = () => moment().format("YYYY-MM-DD");

const isFuture = (date) => !!date && moment(date).isAfter(moment(), "day");

/**
 * Assign / transfer with an effective date. A date after today is scheduled (the balance on that
 * day is not known yet); a transfer, or an assign dated in the past, goes through the settings
 * effective-date engine, which answers with a preview first and writes on the second submit.
 * `submit` returns true when it handled the save, false when the plain create / transfer applies.
 */
export function useEffectiveSubmit({ mode, values, effective, schedule }) {
	const [preview, setPreview] = useState(null);
	const [previewing, setPreviewing] = useState(false);

	const submit = useCallback(async (data) => {
		const effectiveDate = data.effectiveDate ? moment(data.effectiveDate).format("YYYY-MM-DD") : undefined;
		const payload = { plan: data.plan, amount: data.amount, comment: data.comment, effectiveDate };
		if (schedule && effectiveDate && effectiveDate > today()) {
			setPreviewing(true);
			await schedule(mode, values?.id, payload);
			setPreviewing(false);
			return true;
		}
		// transfers always go through the engine (today's date too: it moves unlocked pay periods
		// with the balance); an assign only when it is backdated
		const backdated = effectiveDate && effectiveDate < today();
		if (effective && effectiveDate && (mode === "transfer" || backdated)) {
			setPreviewing(true);
			const response = await effective(mode, values?.id, payload, !!preview);
			setPreviewing(false);
			if (response && !preview) {
				setPreview(response);
			}
			return true;
		}
		return false;
	}, [mode, values, effective, schedule, preview]);

	return { preview, setPreview, previewing, submit };
}

export function EffectiveDateFields({ mode, t, dateFormat }) {
	return (
		<>
			<Field
				name="effectiveDate"
				label={t("effective-date")}
				className="d-flex flex-column"
				rules={[{ required: true, message: t("this-field-is-required") }]}
			>
				<DateInput placeholderText={t("effective-date")} />
			</Field>
			<BaseField shouldUpdate>
				{(_control, _meta, { getFieldValue }) => {
					const date = getFieldValue("effectiveDate");
					if (!isFuture(date)) {
						return null;
					}
					return (
						<div className="border rounded p-2 mb-3 small text-info">
							{t(mode === "transfer" ? "scheduled-transfer-future-note" : "scheduled-assign-future-note", {
								date: moment(date).format(dateFormat),
							})}
						</div>
					);
				}}
			</BaseField>
		</>
	);
}

export function SubmitLabel({ t, preview, withDate = true }) {
	return (
		<BaseField shouldUpdate>
			{(_control, _meta, { getFieldValue }) => {
				if (preview) return t("confirm");
				return withDate && isFuture(getFieldValue("effectiveDate")) ? t("schedule-change") : t("save");
			}}
		</BaseField>
	);
}

const hours = (value) => Number(value || 0).toFixed(2);

// what a backdated assign / transfer will do, from the settings preview
export function EffectivePreview({ preview, t, dateFormat }) {
	const { target, source, warnings = [], blocked = [] } = preview;
	return (
		<div className="border rounded p-2 mb-3 small">
			<div className="font-weight-bold mb-1">
				{t("effective-date-preview", { date: moment(preview.effectiveDate).format(dateFormat) })}
			</div>
			{source && (
				<div>
					{t("effective-carried-balance", { plan: source.planCode, hours: hours(source.bucketAtDate?.available) })}
				</div>
			)}
			<div>
				{t("effective-opening-balance", { plan: target.planCode, hours: hours(target.opening) })}
			</div>
			{target.movedLogs?.length > 0 && (
				<div>
					{t("effective-moved-movements", { count: target.movedLogs.length, plan: target.planCode, list: target.movedLogs.join(", ") })}
				</div>
			)}
			<div>
				{t("effective-periods-recalculated", { count: target.periods?.length || 0 })}
			</div>
			<div className="font-weight-bold">
				{t("effective-resulting-balance", { hours: hours(target.bucket?.available), pending: hours(target.bucket?.pending) })}
			</div>
			{warnings.map((warning) => (
				<div key={warning} className="text-warning">{formatDatesInText(warning, dateFormat)}</div>
			))}
			{blocked.map((reason) => (
				<div key={reason} className="text-danger">{formatDatesInText(reason, dateFormat)}</div>
			))}
		</div>
	);
}
